import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAddons, fetchAddonCategories } from "../../services/addonService";
import { getAddonIcon, getCategoryIcon } from "../../utils/addonIcons";
import { readStoredPromo } from "../../services/promotionService";
import { createSecureOrder, createSecurePaymentSession } from "../../services/secureOrderService";
import { useTranslation } from "../../hooks/useTranslation";
import { useErrorNotification } from "../../hooks/useErrorNotification";
import ErrorNotification from "../shared/ErrorNotification";
import { MEDIA_URL } from '../../config/constants';

const AddOnSelector = () => {
  const navigate = useNavigate();
  const { t, getLocalizedPath } = useTranslation('checkout');
  const { showError, hideError, error } = useErrorNotification();
  const [selectedCategory, setSelectedCategory] = useState("Most Picked");
  const [selectedAddOns, setSelectedAddOns] = useState({});
  const [quantities, setQuantities] = useState({});
  const [addOns, setAddOns] = useState([]);
  const [categories, setCategories] = useState(["Most Picked"]);
  const [loading, setLoading] = useState(true);
  const [expandedAddons, setExpandedAddons] = useState({});
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [imageErrors, setImageErrors] = useState({});

  // Popular addon names for "Most Picked" category - curated selection of most requested items
  // Using partial names to avoid special character matching issues (curly quotes in DB)
  const POPULAR_ADDON_NAMES = [
    "Gunslinger (6",  // Matches "Semente Surfboard - Gunslinger (6'6" Beginners) + Wetsuit"
    "Barbecue Grill",
    "Camping Table",
    "Camping Chair"
  ];

  // Curated "Most Picked" items get a "Popular" badge
  const isPopularAddon = (addon) =>
    POPULAR_ADDON_NAMES.some(name =>
      addon.name.toLowerCase().includes(name.toLowerCase()) ||
      name.toLowerCase().includes(addon.name.toLowerCase())
    );

  const isSurfboardAddon = (addon) => {
    const n = (addon.name || '').toLowerCase();
    return n.includes('semente') || n.includes('surfboard');
  };

  // Load existing selections from sessionStorage
  useEffect(() => {
    const savedAddOns = sessionStorage.getItem("checkoutStep3Data");
    if (savedAddOns) {
      try {
        const parsed = JSON.parse(savedAddOns);
        setSelectedAddOns(parsed.selectedAddOns || {});
        setQuantities(parsed.quantities || {});
      } catch (e) {
        console.error("Error parsing step3 data:", e);
      }
    }
  }, []);

  // Fetch add-ons and categories from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [addonsData, categoriesData] = await Promise.all([
          fetchAddons(),
          fetchAddonCategories()
        ]);

        // Transform database add-ons to match component format
        const transformedAddons = addonsData.map(addon => {
          const iconName = addon.IconName?.String || addon.IconName;
          const categoryName = addon.CategoryName?.String || addon.CategoryName;
          const pricingTypeName = addon.PricingTypeName?.String || addon.PricingTypeName || '';

          // Extract both daily_rate and flat_rate
          const dailyRate = parseFloat(addon.DailyRate?.String || '0');
          const flatRate = parseFloat(addon.FlatRate?.String || '0');

          // Extract image URL (stored as array in DB, take first one)
          const imageUrlRaw = addon.ImageUrl || addon.image_url || [];
          const imageUrl = Array.isArray(imageUrlRaw) && imageUrlRaw.length > 0
            ? imageUrlRaw[0]
            : (typeof imageUrlRaw === 'string' ? imageUrlRaw : null);

          return {
            id: addon.ID,
            name: addon.Name?.String || addon.Name || '',
            description: addon.Description?.String || addon.Description || '',
            dailyRate: dailyRate,
            flatRate: flatRate,
            pricingType: pricingTypeName.toLowerCase(), // "daily" or "flat_rate"
            price: dailyRate > 0 ? dailyRate : flatRate, // Display price (for UI)
            category: categoryName || "Miscellaneous",
            icon: iconName ? getAddonIcon(iconName) : getCategoryIcon(categoryName),
            hasQuantity: (addon.MaxQuantity?.Int32 || addon.MaxQuantity || 1) > 1,
            imageUrl: imageUrl
          };
        });

        setAddOns(transformedAddons);

        // Open details by default for surfboards so the board image + description show immediately
        const defaultExpanded = {};
        transformedAddons.forEach(a => {
          const n = (a.name || '').toLowerCase();
          if (n.includes('semente') || n.includes('surfboard')) {
            defaultExpanded[a.id] = true;
          }
        });
        setExpandedAddons(defaultExpanded);

        // Set categories with "Most Picked" as first option (removed "All" category)
        const categoryNames = categoriesData.map(cat => cat.Name?.String || cat.Name || cat);
        setCategories(["Most Picked", ...categoryNames]);
        
      } catch (error) {
        console.error("Error loading add-ons data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Save to sessionStorage whenever selections change
  const updateSessionStorage = (newSelectedAddOns, newQuantities) => {
    const step3Data = {
      selectedAddOns: newSelectedAddOns,
      quantities: newQuantities,
    };
    sessionStorage.setItem("checkoutStep3Data", JSON.stringify(step3Data));
    window.dispatchEvent(new Event("bookingUpdated"));
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  const toggleAddonDetails = (addonId) => {
    setExpandedAddons(prev => ({
      ...prev,
      [addonId]: !prev[addonId]
    }));
  };

  const handleAddOnToggle = (addonId) => {
    const newSelectedAddOns = {
      ...selectedAddOns,
      [addonId]: !selectedAddOns[addonId],
    };
    setSelectedAddOns(newSelectedAddOns);

    // If unselecting, reset quantity to 1
    let newQuantities = { ...quantities };
    if (!newSelectedAddOns[addonId]) {
      newQuantities[addonId] = 1;
      setQuantities(newQuantities);
    }

    updateSessionStorage(newSelectedAddOns, newQuantities);
  };

  const handleQuantityChange = (id, change) => {
    const newQuantities = {
      ...quantities,
      [id]: Math.max(1, (quantities[id] || 1) + change),
    };
    setQuantities(newQuantities);
    updateSessionStorage(selectedAddOns, newQuantities);
  };

  const handleImageError = (addonId) => {
    setImageErrors(prev => ({
      ...prev,
      [addonId]: true
    }));
  };

  // NOTE: currently unreferenced — the checkout flow proceeds via another path.
  // Left in place rather than deleted because it is the full payment handler;
  // decide whether to wire it back up or remove it deliberately.
  // eslint-disable-next-line no-unused-vars
  const handleProceedToPayment = async () => {
    // Prevent double-clicks
    if (isProcessingPayment) {
      console.log('⚠️ Payment already in progress, ignoring click');
      return;
    }

    setIsProcessingPayment(true);

    try {
      // Gather all booking data from sessionStorage (NO PRICING DATA)
      const camperBooking = JSON.parse(sessionStorage.getItem("bookingData") || "{}");
      const step1Data = JSON.parse(sessionStorage.getItem("checkoutStep1Data") || "{}");
      const step2Data = JSON.parse(sessionStorage.getItem("checkoutStep2Data") || "{}");
      const step3Data = JSON.parse(sessionStorage.getItem("checkoutStep3Data") || "{}");

      // Validate critical booking data before proceeding
      if (!camperBooking.vehicleId) {
        throw new Error(t('step3.errors.vehicleNotSelected'));
      }
      if (!camperBooking.nights || !camperBooking.startDate || !camperBooking.endDate) {
        throw new Error(t('step3.errors.datesNotSelected'));
      }
      if (!step2Data.selectedPlan) {
        throw new Error("Please select an insurance plan in step 2.");
      }
      if (!step1Data.firstName || !step1Data.lastName || !step1Data.email || !step1Data.phone) {
        throw new Error("Required customer information is missing. Please complete step 1.");
      }

      // SECURE: Prepare order data with selections only (no prices)
      const secureOrderData = {
        vehicleId: parseInt(camperBooking.vehicleId), // Backend looks up vehicle price
        nights: parseInt(camperBooking.nights),
        startDate: camperBooking.startDate,
        endDate: camperBooking.endDate,
        pickupLocation: camperBooking.pickupLocation?.name || "",
        dropoffLocation: camperBooking.dropoffLocation?.name || "",
        pickupTime: camperBooking.pickupTime?.time || "",
        dropoffTime: camperBooking.dropoffTime?.time || "",
        adults: parseInt(step1Data.adults) || 1,
        children: parseInt(step1Data.children) || 0,
        pets: step1Data.pets || false,
        numberOfPets: parseInt(step1Data.numberOfPets) || 0,
        selectedInsurance: step2Data.selectedPlan || "",
        // Convert selectedAddOns to the format backend expects
        addOnSelections: convertAddOnSelections(step3Data.selectedAddOns || {}, step3Data.quantities || {}),
        customerInfo: {
          firstName: step1Data.firstName || "",
          lastName: step1Data.lastName || "",
          email: step1Data.email || "",
          phone: step1Data.phone || "",
          country: step1Data.country || "",
          licenseStartDate: step1Data.licenseStartDate || "",
          licenseExpiry: step1Data.licenseExpiry || "",
          vatNumber: step1Data.vatNumber || "",
        },
        // The server re-validates this code and recalculates the discount from
        // its own pricing; the quote shown in step 1 is only a preview.
        promoCode: readStoredPromo()?.code || "",
      };

      // Step 1: Create secure order (server calculates pricing)
      const orderResponse = await createSecureOrder(secureOrderData);

      // Optional: Compare frontend vs backend pricing for debugging
      const frontendTotal = calculateFrontendTotal(camperBooking, step1Data, step2Data, step3Data);
      const backendTotal = orderResponse.pricing?.totalAmount || 0;

      console.log('💰 Pricing comparison:', {
        frontend: {
          total: frontendTotal.toFixed(2),
          breakdown: {
            base: camperBooking.priceBreakdown?.dailyTotal || (camperBooking.basePrice * camperBooking.nights),
            pickup: camperBooking.priceBreakdown?.pickupFee || 0,
            dropoff: camperBooking.priceBreakdown?.dropoffFee || 0,
            time: camperBooking.priceBreakdown?.timeFees || 0,
            insurance: step2Data.selectedPlan ? ((step2Data.selectedPlan === 'basic' ? 0 : step2Data.selectedPlan === 'premium' ? 17 : 23) * camperBooking.nights) : 0,
            pets: (step1Data.pets && step1Data.numberOfPets > 0) ? 50 : 0,
          }
        },
        backend: {
          total: backendTotal.toFixed(2),
          breakdown: orderResponse.pricing
        }
      });

      if (Math.abs(frontendTotal - backendTotal) > 0.01) {
        console.warn('🔍 Pricing difference detected:', {
          frontend: frontendTotal.toFixed(2),
          backend: backendTotal.toFixed(2),
          difference: Math.abs(frontendTotal - backendTotal).toFixed(2)
        });
      }

      // Step 2: Create payment session using server-validated order
      console.log('💳 Creating payment session for order:', orderResponse.orderId);
      const paymentResponse = await createSecurePaymentSession(orderResponse.orderId);
      console.log('💳 Payment session response:', paymentResponse);

      // Extract URL from response (handle multiple possible field names)
      const checkoutUrl = paymentResponse.url ||
                          paymentResponse.sessionUrl ||
                          paymentResponse.checkoutUrl ||
                          paymentResponse.session_url ||
                          paymentResponse.checkout_url;

      if (!checkoutUrl) {
        console.error('❌ No checkout URL found in payment response:', paymentResponse);

        // Show user-friendly error with backend pricing
        showError(
          `${t('step3.errors.paymentSessionFailed')} - Order ID: ${orderResponse.orderId}, Expected total: €${backendTotal.toFixed(2)}, Error: ${paymentResponse.error || 'Unknown error'}`
        );
        throw new Error(paymentResponse.error || 'No checkout URL received from payment session');
      }

      console.log('✅ Redirecting to Stripe checkout:', checkoutUrl);
      // Redirect to Stripe checkout
      window.location.href = checkoutUrl;
      
    } catch (error) {
      console.error("Error proceeding to payment:", error);
      showError("Failed to proceed to payment. Please try again.");
      setIsProcessingPayment(false);
    }
  };

  // Add-ons is now the FIRST checkout step — selections are already persisted to
  // sessionStorage on change, so just advance to the customer-details step.
  const handleContinue = () => {
    navigate(getLocalizedPath("/checkout/step2"));
  };

  // Helper function to convert frontend add-on selections to backend format
  const convertAddOnSelections = (selectedAddOns, quantities) => {
    const selections = {};
    Object.keys(selectedAddOns).forEach(addOnId => {
      if (selectedAddOns[addOnId]) {
        selections[addOnId] = quantities[addOnId] || 1;
      }
    });
    return selections;
  };

  // Helper function to calculate frontend total for comparison
  const calculateFrontendTotal = (camperBooking, step1Data, step2Data, step3Data) => {
    // Use the breakdown from EnhancedBookingForm if available (includes fees)
    let baseTotal = (camperBooking.basePrice || 0) * (camperBooking.nights || 1);
    let pickupFee = 0;
    let dropoffFee = 0;
    let timeFees = 0;

    if (camperBooking.priceBreakdown) {
      baseTotal = camperBooking.priceBreakdown.dailyTotal;
      pickupFee = camperBooking.priceBreakdown.pickupFee || 0;
      dropoffFee = camperBooking.priceBreakdown.dropoffFee || 0;
      timeFees = camperBooking.priceBreakdown.timeFees || 0;
    }

    const insurancePrices = { basic: 0, premium: 17, ultimate: 23 };
    const insuranceTotal = step2Data.selectedPlan ?
      insurancePrices[step2Data.selectedPlan] * (camperBooking.nights || 1) : 0;

    const petsFee = step1Data.pets && step1Data.numberOfPets > 0 ? 50 : 0;

    // Frontend add-ons calculation (for comparison) - MUST match backend logic
    let addOnsTotal = 0;
    addOns.forEach(addon => {
      const addonId = String(addon.id);
      if (step3Data.selectedAddOns?.[addonId]) {
        const quantity = step3Data.quantities?.[addonId] || 1;

        // Calculate based on pricing type (daily vs flat rate)
        if (addon.pricingType === 'daily' && addon.dailyRate > 0) {
          // Daily rate: dailyRate * nights * quantity
          addOnsTotal += addon.dailyRate * (camperBooking.nights || 1) * quantity;
        } else {
          // Flat rate: flatRate * quantity
          addOnsTotal += addon.flatRate * quantity;
        }
      }
    });

    // All prices already include 23% Portugal VAT
    const total = baseTotal + pickupFee + dropoffFee + timeFees + insuranceTotal + petsFee + addOnsTotal;
    return total;
  };

  return (
    <div className="addon-selector">
      {error && (
        <ErrorNotification
          message={error}
          onDismiss={hideError}
        />
      )}
      <div className="addon-header">
        <h1 className="addon-title">{t('step3.title')}</h1>
        <p className="addon-description">
          {t('step3.subtitle')}
        </p>
      </div>

      <div className="addon-form-container">
        <h2 className="addon-form-title">{t('step3.formTitle')}</h2>

        <div className="category-chips">
          {categories.map((category) => (
            <button
              key={category}
              className={`category-chip ${selectedCategory === category ? "active" : ""}`}
              onClick={() => handleCategoryClick(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="loading-message">Loading add-ons...</div>
        ) : (
          <div className="addons-list">
            {addOns
              .filter(addon => {
                if (selectedCategory === "Most Picked") {
                  // Show popular items for "Most Picked" category
                  return POPULAR_ADDON_NAMES.some(name =>
                    addon.name.toLowerCase().includes(name.toLowerCase()) ||
                    name.toLowerCase().includes(addon.name.toLowerCase())
                  );
                }
                return addon.category === selectedCategory;
              })
              .map((addon) => (
              <div key={addon.id} className={`addon-item ${expandedAddons[addon.id] ? 'expanded' : ''}`}>
                <div className="addon-main-row">
                  <div className="addon-left">
                    <div
                      className="addon-checkbox"
                      onClick={() => handleAddOnToggle(addon.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <div
                        className="checkbox-container"
                        style={{
                          backgroundColor: selectedAddOns[addon.id]
                            ? "#009B31"
                            : "transparent",
                          border: selectedAddOns[addon.id]
                            ? "2px solid #009B31"
                            : "2px solid #ccc",
                          width: "20px",
                          height: "20px",
                          borderRadius: "4px",
                          transition: "all 0.2s ease",
                        }}
                      ></div>
                    </div>
                    <div className="addon-info">
                      {addon.imageUrl && addon.imageUrl.trim() !== '' && !imageErrors[addon.id] ? (
                        <img
                          className="addon-thumb"
                          src={addon.imageUrl}
                          alt={addon.name}
                          loading="lazy"
                          onError={() => handleImageError(addon.id)}
                        />
                      ) : (
                        <div className="addon-icon">{addon.icon}</div>
                      )}
                      <div className="addon-text">
                        {isPopularAddon(addon) && (
                          <span className="addon-badge addon-badge--popular">{t('step3.popularBadge')}</span>
                        )}
                        <h3 className="addon-name">{addon.name}</h3>
                        {addon.description && addon.description.trim() !== '' && (
                          <p className="addon-desc-teaser">{addon.description}</p>
                        )}
                        {addon.name.toLowerCase().includes('semente') && (
                          <a
                            className="addon-partner-link"
                            href="https://www.semente.pt"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {t('step3.bySemente')}
                          </a>
                        )}
                        {isSurfboardAddon(addon) && (
                          <span className="addon-social-proof">{t('step3.socialProofSurf')}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="addon-right">
                    {(addon.description || addon.imageUrl) && (
                      <button
                        className={`addon-details-toggle ${expandedAddons[addon.id] ? 'open' : ''}`}
                        onClick={() => toggleAddonDetails(addon.id)}
                        aria-label="Toggle details"
                      >
                        <span className="toggle-text">{expandedAddons[addon.id] ? 'Hide' : 'Details'}</span>
                        <span className="toggle-arrow">›</span>
                      </button>
                    )}
                    {addon.hasQuantity && selectedAddOns[addon.id] && (
                      <div className="quantity-controls">
                        <button
                          className="quantity-btn"
                          onClick={() => handleQuantityChange(addon.id, -1)}
                        >
                          <img
                            src={`${MEDIA_URL}/icons/navigation/minus.svg`}
                            alt="minus"
                          />
                        </button>
                        <span className="quantity-value">
                          {quantities[addon.id] || 1}
                        </span>
                        <button
                          className="quantity-btn"
                          onClick={() => handleQuantityChange(addon.id, 1)}
                        >
                          <img
                            src={`${MEDIA_URL}/icons/navigation/plus.svg`}
                            alt="plus"
                          />
                        </button>
                      </div>
                    )}
                    <div className="addon-price">
                      €{selectedAddOns[addon.id] && addon.hasQuantity
                        ? (addon.price * (quantities[addon.id] || 1)).toFixed(2)
                        : addon.price.toFixed(2)}
                      <span className="price-suffix">{addon.pricingType === 'daily' ? '/day' : '/unit'}</span>
                    </div>
                  </div>
                </div>

                {expandedAddons[addon.id] && addon.description && (
                  <div className="addon-details">
                    {(addon.imageUrl && addon.imageUrl.trim() !== '' && !imageErrors[addon.id]) && (
                      <div className="addon-image">
                        <img
                          src={addon.imageUrl}
                          alt={addon.name}
                          onError={() => handleImageError(addon.id)}
                          style={{ objectFit: 'cover', width: '100%', height: 'auto' }}
                        />
                      </div>
                    )}
                    {addon.description && (
                      <p className="addon-description">{addon.description}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <button
          className="proceed-button"
          onClick={handleContinue}
        >
          {t('step3.continue')}
        </button>
      </div>
    </div>
  );
};

export default AddOnSelector;
